import React from "react";
import { Route } from "react-router-dom";
import type { RouteProps } from "react-router-dom";

import { showToast } from "@/providers/ToastProvider";
import { Spinner2 } from "@/components/UI2/atoms/Spinner";

import { ACCESS_DENIED_ROLE_MESSAGE } from "../constants/messages";
import type { PermissionKey } from "../types";
import { useCheckPermission } from "../hooks/useCheckPermission";

type ProtectedRouteProps = {
  deniedMessage?: string;
  fallbackRoute?: string;
  permissionKey: PermissionKey;
} & RouteProps;

export function ProtectedRoute({
  component: Component,
  deniedMessage = ACCESS_DENIED_ROLE_MESSAGE,
  fallbackRoute = "/app/home",
  permissionKey,
  ...routeProps
}: ProtectedRouteProps) {
  const hasPermission = useCheckPermission({ permissionKey });

  // Using history.replace instead of <Redirect> to avoid render loops
  return (
    <Route
      {...routeProps}
      render={(props) => {
        // Show spinner while role/profile are loading
        if (hasPermission === null) {
          return (
            <div className="tw:flex tw:items-center tw:justify-center tw:min-h-100">
              <Spinner2 />
            </div>
          );
        }

        const isFallbackRoute = props.location.pathname === fallbackRoute;

        if (!hasPermission && !isFallbackRoute) {
          props.history.replace(fallbackRoute);
          showToast([
            {
              heading: "Access Denied",
              text: deniedMessage,
              variant: "error",
            },
          ]);
        }

        // Early return for any non-render cases
        if (!hasPermission || !Component) return null;

        // Render the protected component with route props
        return <Component {...props} />;
      }}
    />
  );
}
